import ActionCheck from "@material-ui/icons/CheckCircle";
import AlertError from "@material-ui/icons/ErrorOutline";

type ConfirmDialogProps = Pick<
  IConfirmAndMutateProps,
  "translate" | "dialog" | "dialogOptions"
>;

const confirmDialog = async ({
  translate,
  dialog,
  dialogOptions,
}: ConfirmDialogProps) =>
  dialog
    .confirm({
      title: translate(dialogOptions?.title || "portal.dialog.confirm"),
      message: translate(dialogOptions?.message || ""),
      ok: { text: "Confirm", startIcon: <ActionCheck /> },
      cancel: { text: "Cancel", startIcon: <AlertError /> },
    })
    .then((value) => value)
    .catch(() => undefined);

export interface IConfirmAndMutateProps {
  resource: string;
  mutate: any /* RA type: UseMutationValue; */;
  type?: "create" | "update";
  redirect: (url: string) => void;
  values: any;
  translate: any /* RA type: Translate; */;
  dialog?: any;
  dialogOptions?: {
    title?: string;
    message?: string;
  };
  transform?: (any) => any;
  notify: any;
  onSuccess?: () => void;
  successNotificationMessage?: string;
}

/**
 * Function to provide a confirmation dialog, and then request a promise-based mutation.
 *
 * Why? This is RA's pattern for including server-side validation in a mutating call.
 *
 * By wrapping in an async function, some RA magic will wait on the result, and if
 * errors are returned, inject these into the form.
 *
 * Now idea if there is a cleaner way to do this...
 *
 *
 * @example
 *
 * const [mutate] = useMutation();
 * const notify = useNotify();
 * const redirect = useRedirect();
 * const translate = useTranslate();
 *
 * // Define the callback for the save function
 * const save = useCallback(
 *   async (values) => {
 *     return await confirmAndMutate({
 *       resource: "orgs",
 *       mutate,
 *       redirect,
 *       values,
 *       notify,
 *     });
 *   },
 *   [mutate, notify, redirect]
 * );
 *
 * // Set the save prop in your form
 * <Create ...>
 *  <SimpleForm save={save}>
 *  ...
 *  </SimpleForm>
 * </Create>
 */

export const confirmAndMutate = async ({
  resource,
  mutate,
  redirect,
  type = "create",
  values,
  translate,
  transform = (v) => v,
  dialog,
  dialogOptions,
  notify,
  successNotificationMessage = "portal.messages.created",
}: IConfirmAndMutateProps) => {
  const isCreate = type === "create";

  if (dialog) {
    const confirmation = await confirmDialog({
      translate,
      dialog,
      dialogOptions,
    })
      .then((value) => value)
      .catch(() => undefined);

    if (!confirmation) {
      notify("portal.messages.cancelled", "warning");
      return;
    }
  }

  const payload = isCreate
    ? {
        data: transform(values),
      }
    : { id: values.id, data: transform(values) };

  try {
    await mutate(
      {
        type,
        resource,
        payload,
      },
      {
        returnPromise: true,
        onSuccess: () => {
          redirect(`/${resource}`);
          notify(successNotificationMessage);
        },
      }
    );
  } catch (error: any) {
    const apierror = error.body?.apierror;

    if (apierror?.message) {
      notify(apierror.message, "warning");
    } else {
      notify("portal.messages.serverError", "warning");
    }

    if (apierror?.status === "CONFLICT") {
      /*
       * For validation, the error must be keyed by a source attribute name
       */
      const errors = {};
      apierror.subErrors?.forEach(
        (error) => (errors[`${error.field}`] = error.message)
      );

      return errors;
    }
  }
};
